import * as React from 'react';

import Summary from './components/Summary';
import CreateList from './components/CreateList';
import ItemsList from './components/ItemsList';

import ListModel from 'src/model/list.model';
import UserModel from 'src/model/user.model';
import { IListsError, IListsSuccess } from 'src/store/modules/lists/reducers';
import { ICartError, ICartSuccess } from 'src/store/modules/cart/reducers';
import { IWarrantyProvider } from 'src/model/warranty.model';
import { ISelectOption } from 'src/components/common/Select2/components/SelectOption/SelectOption';
import { ICartServiceItem } from 'src/services/cart/CartService';

import './Lists.scss';

interface IListsProps {
  lists: ListModel[];
  currentList: ListModel;
  isPending: boolean;
  isUpdating: boolean;
  userData: UserModel;
  error: IListsError;
  success: IListsSuccess;
  cartError: ICartError;
  cartSuccess: ICartSuccess;
  warranties: IWarrantyProvider[];
  cartIsPending: boolean;
  onLoad(): void;
  onClearList(): void;
  onGetList(id: number): any;
  onClearLists(id: number): any;
  onCreateList(name: string): any;
  onUpdateList(id: number, name: string): void;
  onDeleteList(id: number): void;
  onItemUpdate(
    itemId: number,
    name: string,
    mfc: string,
    quantity: number,
    shipmentMarking: string,
    warrantyProvider: string,
    dispatch: string
  ): void;
  onRemoveItem(itemId: number): void;
  onClearfavList?: () => void;
  onItemDetailsUpdate(itemId: number): void;
  onItemsDetailsUpdate(itemsIds: number[]): void;
  onAddToCart(
    claims: string,
    delivery: string,
    deliveryLocation: string,
    oneTimeAddress: null,
    partNumber: string,
    quantity: number,
    rushOrder: boolean,
    shipmentMarking: string,
    shipsfrom: string,
    ticketNumber: string,
    warrantyProvider: string
  ): void;
  onAddListToCart(items: ICartServiceItem[]): void;
  onGetTopProducts(): void;
  onGetCommonPurchases(): void;
  onClearAlerts(): void;
  onClearRemoveAlerts():void;
  t?: any;
}

interface IListsState {
  selectedList: number | string; // send id for normal list, and send 'favorites', 'common' or 'top' for special lists
  showCreateList: boolean;
}

class Lists extends React.Component<IListsProps, IListsState> {
  constructor(props: any) {
    super(props);

    this.state = {
      selectedList: 0,
      showCreateList: false
    };
  }

  private handleClickFavorites = () => {
    this.props.onClearAlerts();
    const favorite = this.props.lists.find(list => list.favorite);
    if (favorite) {
      this.props.onGetList(favorite.listNumber);
    }
    this.setState({ selectedList: 'favorites', showCreateList: false });
  };
  private handleClearAllParts = (id: number) => {
    this.props.onClearLists(id);
  };

  private handleClickCommonPurchases = () => {
    this.props.onClearAlerts();
    this.props.onGetCommonPurchases();
    this.setState({ selectedList: 'common', showCreateList: false });
  };

  private handleClickTopParts = () => {
    this.props.onClearAlerts();
    this.props.onGetTopProducts();
    this.setState({ selectedList: 'top', showCreateList: false });
  };

  private handleClickList = (id: number) => {
    this.props.onClearAlerts();
    this.setState({ selectedList: id, showCreateList: false });
    this.props.onGetList(id);
  };

  private selectNewList = (name: string) => {
    this.props.onClearAlerts();
    this.setState({ selectedList: name, showCreateList: false });
  };

  private handleShowHideCreateList = () => {
    this.setState(prevState => ({ showCreateList: !prevState.showCreateList }));
  };

  private handleCreateList = (name: string) => {
    this.handleShowHideCreateList();
    this.props.onCreateList(name);
    this.selectNewList(name);
  };

  private getWarrantiesOptions = () => {
    const options: ISelectOption[] = [];
    this.props.warranties.forEach(warranty => {
      options.push({
        name: warranty.warrantyClaimNumber,
        value: warranty.providerNumberString
      });
    });
    return options;
  };

  private handleDeleteList = (listId: number) => {
    this.props.onDeleteList(listId);
    this.setState({ selectedList: 0 });
  };

  public componentDidUpdate(prevProps: IListsProps) {
    if (prevProps.lists.length === 0 && prevProps.lists !== this.props.lists) {
      this.handleClickFavorites();
    }
  }

  public componentDidMount() {
    this.props.onLoad();
    this.props.onClearAlerts();
    this.handleClickFavorites();
  }

  public componentWillUnmount() {
    this.props.onClearList();
    this.props.onClearAlerts();
  }

  private renderStartScreen = () => {
    const { t } = this.props;
    return (
      <div className="rp-lists--start-screen">
        <div className="rp-lists--start-screen--title">
          {t('myFavourites.createNewList')}
        </div>
        <div className="rp-lists--start-screen--subtitle">
          {t('myFavourites.startBuildingLists')}
        </div>
      </div>
    );
  };

  private renderListItems = () => {
    return (
      <div>
        {this.state.selectedList && this.props.currentList.name ? (
          <ItemsList
            updating={this.props.isUpdating}
            list={this.props.currentList}
            userData={this.props.userData}
            error={this.props.error}
            success={this.props.success}
            cartError={this.props.cartError}
            cartSuccess={this.props.cartSuccess}
            warranties={this.getWarrantiesOptions()}
            handleUpdateList={this.props.onUpdateList}
            handleDeleteList={this.handleDeleteList}
            // onClear={this.props.onClearfavList()}
            handleItemUpdate={this.props.onItemUpdate}
            handleRemoveItem={this.props.onRemoveItem}
            handleItemDetailsUpdate={this.props.onItemDetailsUpdate}
            handleItemsDetailsUpdate={this.props.onItemsDetailsUpdate}
            handleAddToCart={this.props.onAddToCart}
            onClearLists={this.props.onClearLists}
            handleAddListToCart={this.props.onAddListToCart}
            closeAlertMessage={this.props.onClearAlerts}
            closeRemoveAlertMessage={this.props.onClearRemoveAlerts}
            cartIsPending={this.props.cartIsPending}
          />
        ) : (
          this.renderStartScreen()
        )}
      </div>
    );
  };

  public render() {
    return (
      <div className="rp-lists">
        {this.props.isPending ? (
          <div className="loader-container">
            <div className="loader" />
          </div>
        ) : (
          <React.Fragment>
            <div className="rp-lists-summary-area">
              <Summary
                selectedList={this.state.selectedList}
                lists={this.props.lists}
                onCreateList={this.handleShowHideCreateList}
                onClickFavorites={this.handleClickFavorites}
                onClickCommonPurchases={this.handleClickCommonPurchases}
                onClickTopParts={this.handleClickTopParts}
                onClickList={this.handleClickList}
              />
            </div>
            <div className="rp-lists-main-area" id="rp-lists-main-area">
              {this.state.showCreateList ? (
                <CreateList
                  onClose={this.handleShowHideCreateList}
                  onCreate={this.handleCreateList}
                />
              ) : (
                this.renderListItems()
              )}
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default Lists;
